import {
  Business,
  Contact,
  CrossReference,
  Household,
  LexicalText,
  Meeting,
  Mention,
  PaginationMetadata,
  Project,
  Tag,
  TeamPreview,
  User,
} from "@/schema"

export interface TaskAttachment {
  file_name: string
  file_type: string
  file_size: number
  source: "uploaded"
  signed_id: string
  download_url: string
  view_url?: string
}

// A link to a file or folder in the book's storage integration (Google Drive, etc.) —
// no bytes are stored in Slant. Rendered alongside uploaded files in attachments arrays.
export interface StorageAttachmentMeta {
  id: string
  file_name: string
  file_type: string | null
  source: "storage"
  kind: "file" | "folder"
  external_id: string
  web_url: string
  provider: string
  mode: "reference" | "copy_per_instance"
  copy_status: "none" | "pending" | "completed" | "failed"
}

export type AnyAttachment = TaskAttachment | StorageAttachmentMeta

export function isStorageAttachment(attachment: AnyAttachment): attachment is StorageAttachmentMeta {
  return attachment.source === "storage"
}

export function isUploadedAttachment(attachment: AnyAttachment): attachment is TaskAttachment {
  return attachment.source === "uploaded"
}

export interface TaskActionable {
  type: string
  id: string
  [key: string]: string | number | boolean | null | undefined
}

export interface MilestoneOutcomeRequirementOutcome {
  id: string
  label: string
  default_comment?: string | null
  destination: "next_milestone" | "specific_milestone" | "complete_project"
  target_project_milestone_id?: string | null
  target_project_milestone_title?: string | null
  destination_form_fields?: MilestoneFormRequirementField[]
}

export interface MilestoneOutcomeRequirement {
  project_id: string
  project_name: string
  project_template_id?: string
  household_id?: string | null
  household_has_storage_folder?: boolean
  book_id?: string | null
  book_name?: string | null
  milestone_id: string
  milestone_title: string
  outcomes: MilestoneOutcomeRequirementOutcome[]
}

export interface OutcomeRequiredErrorResponse {
  error: string
  error_code: "outcome_required"
  milestone_outcome_requirement: MilestoneOutcomeRequirement
}

export interface MilestoneFormRequirementField {
  id: string
  label: string
  custom_field_id?: string | null
  field_type:
    | "text"
    | "textarea"
    | "number"
    | "date"
    | "select"
    | "boolean"
    | "currency"
    | "household_record"
    | "file_attachment"
  required: boolean
  placeholder?: string | null
  select_options: string[] | null
  household_record_source?: "custodian_account" | "reporting_account" | "person" | "trust" | "address" | null
  has_response?: boolean
  current_value?: string | number | boolean | { storage_file_id: string; file_name: string; file_type?: string } | null
}

export interface MilestoneFormRequirement {
  project_id: string
  project_name: string
  project_template_id?: string
  household_id?: string | null
  household_has_storage_folder?: boolean
  milestone_id: string
  milestone_title: string
  fields: MilestoneFormRequirementField[]
}

export interface FormFieldsRequiredErrorResponse {
  error: string
  error_code: "form_fields_required"
  milestone_form_requirement: MilestoneFormRequirement
}

export enum TaskStatus {
  TODO = "todo",
  DONE = "done",
  REJECTED = "rejected",
  SCHEDULED = "scheduled",
  PENDING = "pending",
}

export type TaskStatusOptions = TaskStatus

export type TaskPriority = "low" | "medium" | "high" | "urgent" | null

// Type guard functions to safely narrow task.taskable based on task.taskable_type
export function isTaskableHousehold(
  taskable: Household | Contact | Business | null | undefined,
  taskableType?: string | null
): taskable is Household {
  return (
    taskable !== null &&
    taskable !== undefined &&
    (taskableType === "Household" ||
      taskableType === "Client" ||
      taskableType === "Prospect" ||
      taskableType === "PastClient")
  )
}

export function isTaskableContact(
  taskable: Household | Contact | Business | null | undefined,
  taskableType?: string | null
): taskable is Contact {
  return taskable !== null && taskable !== undefined && taskableType === "Contact"
}

export function isTaskableBusiness(
  taskable: Household | Contact | Business | null | undefined,
  taskableType?: string | null
): taskable is Business {
  return taskable !== null && taskable !== undefined && taskableType === "Business"
}

export interface Task {
  id: string
  book_id?: string
  book_name?: string
  book_initials?: string
  deadline_at?: Date | null
  completed_at?: Date | null
  created_at: Date
  updated_at: Date
  created_by?: User
  created_by_id?: string
  completed_by?: User | null
  actionable?: TaskActionable | null
  collaborators?: User[]
  collaborator_ids?: string[]
  optimistic_status?: TaskStatus
  assigned_to?: User | null
  assigned_to_id?: string | null
  assigned_team?: TeamPreview | null
  assigned_team_id?: string | null
  status: TaskStatus
  accepted_at?: Date | null
  description?: LexicalText
  title?: LexicalText
  groupKey?: string
  groupKeyTotalCount?: number
  household?: Household
  household_id?: string | null
  contact?: Contact
  contact_id?: string | null
  taskable_type?: string | null
  taskable_id?: string | null
  taskable?: Household | Contact | Business | null
  transcript_timestamp?: number
  was_recommended?: boolean
  project_id?: string | null
  project?: Project
  project_milestone_id?: string | null
  recommendable_type?: string
  recommendable_id?: string
  recommendable?: Meeting
  syncbox_thread_id?: string | null
  task_recurrence_id?: string
  task_recurrence?: TaskRecurrenceMeta
  comment_count?: number
  priority?: TaskPriority | null
  tag_ids?: string[]
  tags?: Tag[]
  mentions?: CrossReference[] | Mention[]
  attachments?: AnyAttachment[]
  all_day?: boolean
  is_private?: boolean
}

export interface TaskRecurrenceMeta {
  id: string
  active: boolean
  interval_value: number
  interval_unit: "days" | "weeks" | "months" | "years"
  monthly_rule_type?: "calendar_day" | "day_of_week" | "last_day"
  weekday?: "sunday" | "monday" | "tuesday" | "wednesday" | "thursday" | "friday" | "saturday" | null
  weekday_ordinal?: "first" | "second" | "third" | "fourth" | "last" | null
  next_run_at?: string
  start_at?: string
  recurring_until?: string
  deactivated_at?: string
  has_unpropagated_changes?: boolean
}

export interface CreateTaskRecurrencePayload {
  schwab_activity_id?: string
  interval_value: number
  interval_unit: "days" | "weeks" | "months" | "years"
  monthly_rule_type?: "calendar_day" | "day_of_week" | "last_day"
  weekday?: "sunday" | "monday" | "tuesday" | "wednesday" | "thursday" | "friday" | "saturday" | null
  weekday_ordinal?: "first" | "second" | "third" | "fourth" | "last" | null
  start_at?: string
  recurring_until?: string
  all_day?: boolean
  collaborator_ids?: string[]
  taskable_type?: string
  taskable_id?: string
  household_id?: string
  contact_id?: string
  task_template_attributes: {
    assigned_to_id?: string
    title?: LexicalText
    description?: LexicalText
    priority?: TaskPriority | null
    tag_ids?: string[]
  }
}

export interface ConvertTaskToRecurringPayload {
  interval_value: number
  interval_unit: "days" | "weeks" | "months" | "years"
  monthly_rule_type?: "calendar_day" | "day_of_week" | "last_day"
  weekday?: "sunday" | "monday" | "tuesday" | "wednesday" | "thursday" | "friday" | "saturday" | null
  weekday_ordinal?: "first" | "second" | "third" | "fourth" | "last" | null
  start_at?: string
  recurring_until?: string
}

export interface UpdateTaskRecurrencePayload {
  interval_value: number
  interval_unit: "days" | "weeks" | "months" | "years"
  monthly_rule_type?: "calendar_day" | "day_of_week" | "last_day"
  weekday?: "sunday" | "monday" | "tuesday" | "wednesday" | "thursday" | "friday" | "saturday" | null
  weekday_ordinal?: "first" | "second" | "third" | "fourth" | "last" | null
  start_at?: string
  recurring_until?: string | null
}

export interface CreateTaskPayload {
  schwab_activity_id?: string
  description?: LexicalText
  title?: LexicalText
  deadline_at?: Date
  collaborator_ids?: string[]
  assigned_to_id?: string
  assigned_to_custom_field_id?: string | null
  assigned_team_id?: string | null
  taskable_type?: string
  taskable_id?: string
  household_id?: string
  contact_id?: string
  all_day?: boolean
  status?: TaskStatus
  project_id?: string | null
  project_milestone_id?: string | null
  recommendable_type?: string
  recommendable_id?: string
  syncbox_thread_id?: string
  priority?: TaskPriority | null
  tag_ids?: string[]
  mentions?: Mention[]
  files?: File[]
  template_attachment_signed_ids?: string[]
  template_storage_attachment_ids?: string[]
  book_id?: string
  task_template_id?: string | null
  is_private?: boolean
}

export interface TaskVersionSummary {
  id: string
  created_at: string
  actor: {
    id: string
    name: string
  } | null
}

export type CurrentTaskVersionSummary = Omit<TaskVersionSummary, "id">

export interface TaskVersionsResponse {
  current: CurrentTaskVersionSummary
  versions: TaskVersionSummary[]
  pagination: PaginationMetadata
}

export interface TaskVersionSnapshot extends TaskVersionSummary {
  title: LexicalText
  description: LexicalText
}
